import React, { createContext, useContext } from 'react';
import { ConfiguratorOptionDto } from '@/services/configurator/configurator.types';

export interface SelectOptionParams {
    option: ConfiguratorOptionDto;
    customFieldData: string | null;
    roomId?: number | null;
    // Buyer-entered whole-number quantity, when the group has allowManualQuantity. Omitted otherwise.
    quantity?: number | null;
    // The legacy configurator shows every deadline on one page and selects per the group's own
    // deadline, so it passes this explicitly. The v2 flow omits it and the provider falls back to
    // the single deadline tracked in OptionConfiguratorContext.
    deadlineId?: number | null;
}

// Clear a group's current selection (no option chosen). `roomId`/`deadlineId` follow the same
// resolution rules as SelectOptionParams — omit them to fall back to the URL room and the
// context deadline.
export interface ClearSelectionParams {
    groupId: number;
    roomId?: number | null;
    deadlineId?: number | null;
}

export interface ConfiguratorMutationContextType {
    selectOption: (params: SelectOptionParams) => void;
    // Clear the selection for a group (sends a null option, or drops the stored showroom pick).
    clearSelection: (params: ClearSelectionParams) => void;
    // Apply a sequence of option selections, then resolve once the configurator has been
    // recalculated with the full set. Used by DependencyWizardModal where the trigger option
    // plus a chain of dependent picks must land atomically (from the user's perspective).
    selectOptions: (selections: SelectOptionParams[]) => Promise<void>;
    isSelecting: boolean;
    // `deadlineId` is optional and defaults to the deadline tracked in context; the legacy
    // multi-deadline page passes the preset group's own deadline explicitly.
    pickStyle: (styleId: number, roomId?: number, deadlineId?: number | null) => void;
    isPickingStyle: boolean;
    unpickStyle: (styleGroupId: number, roomId?: number, deadlineId?: number | null) => void;
    isUnpickingStyle: boolean;
}

const ConfiguratorMutationContext = createContext<ConfiguratorMutationContextType | null>(null);

export const ConfiguratorMutationProvider: React.FC<{
    value: ConfiguratorMutationContextType;
    children: React.ReactNode;
}> = ({ value, children }) => {
    return <ConfiguratorMutationContext.Provider value={value}>{children}</ConfiguratorMutationContext.Provider>;
};

export function useConfiguratorMutations(): ConfiguratorMutationContextType {
    const ctx = useContext(ConfiguratorMutationContext);
    if (!ctx) {
        throw new Error(
            'useConfiguratorMutations must be used within a ConfiguratorMutationProvider (e.g. ApiMutationProvider for the authenticated buyer flow, or ShowroomMutationProvider for the public showroom).'
        );
    }
    return ctx;
}
